import dayjs from "dayjs";

export const formatDate = (date: Date): string => {
  return dayjs(date).format("DD/MM/YYYY HH:mm");
};

export const formatDateTime = (date: Date): string => {
  return dayjs(date).format("DD/MM/YYYY HH:mm:ss");
};

export const formatPrice = (price: number | undefined): string => {
  if (price === undefined || price === null) return "-";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 5,
    useGrouping: true,
  }).format(price);
};

export const cleanAlertText = (text: string | undefined): string => {
  if (!text) return "";
  return text.replace(/^-+\s*/, "").trim();
};

export const getAlertSummary = (alert: any): string => {
  const msg = alert.mainMessage;
  if (msg.teams && msg.teams.length > 0) {
    return `${msg.teams.join(" vs ")} - ${msg.betType || ""} ${
      msg.selection || ""
    }`;
  }
  return msg.text || "Sin descripción";
};

/**
 * Get pip multiplier for a given forex symbol or index type
 * @param symbol - Currency pair (e.g., "EUR/USD", "USDJPY") or index name
 * @param type - "forex" or "synthetic_indices"
 * @returns multiplier to convert price difference to pips/points
 */
export const getPipMultiplier = (
  symbol: string | undefined,
  type: string
): number => {
  if (type === "forex") {
    const sym = (symbol || "").toUpperCase();
    if (sym.includes("JPY")) {
      return 100;
    } else if (sym.includes("XAU") || sym.includes("GOLD")) {
      return 10;
    } else if (sym.includes("XAG") || sym.includes("SILVER")) {
      return 100;
    } else {
      return 10000;
    }
  } else {
    // Synthetic indices: 1 price unit = 1 point
    return 1;
  }
};

/**
 * Infer trading direction based on entry, stop loss, and take profit values
 * @param entry - Entry price
 * @param stopLoss - Stop loss price (optional)
 * @param target - Target/take profit price
 * @returns "BUY" | "SELL" | "INCONSISTENT" | null
 */
export const inferDirection = (
  entry: number,
  stopLoss: number | undefined,
  target: number
): "BUY" | "SELL" | "INCONSISTENT" | null => {
  // For BUY: SL should be lower than entry, TP should be higher
  // For SELL: SL should be higher than entry, TP should be lower

  const hasStopLoss = stopLoss !== undefined && stopLoss !== null;

  if (hasStopLoss) {
    // Use both SL and TP to determine direction
    const slBelowEntry = stopLoss < entry;
    const tpAboveEntry = target > entry;
    const slAboveEntry = stopLoss > entry;
    const tpBelowEntry = target < entry;

    if (slBelowEntry && tpAboveEntry) {
      return "BUY";
    } else if (slAboveEntry && tpBelowEntry) {
      return "SELL";
    } else {
      // Inconsistent data: doesn't match BUY or SELL pattern
      return "INCONSISTENT";
    }
  } else {
    // Only TP available, less reliable but still useful
    if (target > entry) {
      return "BUY"; // TP above entry suggests BUY
    } else if (target < entry) {
      return "SELL"; // TP below entry suggests SELL
    } else {
      // Target equals entry - cannot determine
      return null;
    }
  }
};

export const calculatePips = (
  entry: number | undefined,
  target: number | undefined,
  symbol: string | undefined,
  type: string, // "forex" | "synthetic_indices"
  direction?: string, // "BUY" | "SELL"
  stopLoss?: number // Optional: used for direction inference if direction is missing
): string => {
  if (
    entry === undefined ||
    entry === null ||
    target === undefined ||
    target === null
  ) {
    return "";
  }

  // Calculate raw difference based on direction
  // BUY: Target - Entry (Positive if Target > Entry)
  // SELL: Entry - Target (Positive if Target < Entry)
  const normalizedDirection = direction?.toUpperCase();
  let diff: number;

  if (normalizedDirection === "BUY") {
    diff = target - entry;
  } else if (normalizedDirection === "SELL") {
    diff = entry - target;
  } else {
    // Direction is missing or invalid - try to infer from prices
    const inferred = inferDirection(entry, stopLoss, target);

    if (inferred === "INCONSISTENT") {
      // Data is inconsistent - return error marker
      console.error(
        `[calculatePips] Inconsistent trade data: entry=${entry}, SL=${stopLoss}, target=${target}. Cannot determine direction.`
      );
      return "⚠️ ERROR";
    } else if (inferred === "BUY" || inferred === "SELL") {
      diff = inferred === "BUY" ? target - entry : entry - target;

      // Log warning if direction was inferred (not explicitly provided)
      if (normalizedDirection !== undefined && normalizedDirection !== "") {
        console.warn(
          `[calculatePips] Invalid direction "${direction}" provided. Inferred "${inferred}" from prices (entry=${entry}, SL=${stopLoss}, target=${target}).`
        );
      }
    } else {
      // Could not determine direction
      console.warn(
        `[calculatePips] Cannot determine direction from prices: entry=${entry}, SL=${stopLoss}, target=${target}. Defaulting to BUY calculation.`
      );
      diff = target - entry;
    }
  }

  // Use shared multiplier logic
  const multiplier = getPipMultiplier(symbol, type);
  const pips = diff * multiplier;

  const sign = pips > 0 ? "+" : "";
  // If pips is exactly 0, sign is empty. If pips is negative, it already has the sign.
  // Use Number() to remove trailing zeros (50.0 -> 50, 50.5 -> 50.5)
  return `${sign}${Number(pips.toFixed(1))} ${
    type === "forex" ? "pips" : "pts"
  }`;
};

export const calculateRR = (
  entry: number | undefined,
  stopLoss: number | undefined,
  takeProfit: number[] | undefined
): string => {
  if (
    entry === undefined ||
    entry === null ||
    stopLoss === undefined ||
    stopLoss === null ||
    !takeProfit ||
    takeProfit.length === 0
  ) {
    return "";
  }

  const risk = Math.abs(entry - stopLoss);
  if (risk === 0) return "";

  // Calculate reward based on the last TP value.
  // Assumption: TPs are ordered by distance from entry, so the last TP
  // represents the maximum potential reward (best-case scenario).
  // For BUY orders: last TP should be highest; for SELL orders: last TP should be lowest.
  const maxTp = takeProfit[takeProfit.length - 1];
  const reward = Math.abs(entry - maxTp);

  const ratio = reward / risk;

  return `R: 1:${ratio.toFixed(1)}`;
};

export const getRRColorClass = (
  entry: number | undefined,
  stopLoss: number | undefined,
  takeProfit: number[] | undefined
): string => {
  if (
    entry === undefined ||
    entry === null ||
    stopLoss === undefined ||
    stopLoss === null ||
    !takeProfit ||
    takeProfit.length === 0
  ) {
    return "";
  }

  const risk = Math.abs(entry - stopLoss);
  if (risk === 0) return "";

  const maxTp = takeProfit[takeProfit.length - 1];
  const reward = Math.abs(entry - maxTp);
  const ratio = reward / risk;

  if (ratio < 1) return "text-danger";
  if (ratio < 2) return "text-secondary"; // or text-warning
  return "text-success";
};
